// controllers/walletController.js  (frontend)
import apiService from '../services/apiService';
import { authController } from './authController';

const _getUser = () => authController.getCurrentUser();
const _getRole = () => _getUser()?.role || null;
const _getUid  = () => { const u = _getUser(); return u?.id || u?._id || null; };
const _guard   = (roles) => {
  const role = _getRole();
  if (!role)               return { blocked: true, reason: 'Not authenticated' };
  if (!roles.includes(role)) return { blocked: true, reason: `Access denied. Requires: ${roles.join(' or ')}` };
  return { blocked: false };
};

const walletController = {
  getCurrentUser: () => _getUser(),
  isSuperAdmin:   () => _getRole() === 'super_admin',
  isAdmin:        () => ['admin','super_admin'].includes(_getRole()),
  getCurrentUid:  () => _getUid(),

  // ── Wallet Overview ────────────────────────────────────────────────────────
  getWalletOverview: async (userId) => {
    const g = _guard(['admin','super_admin','finance_manager','accountant']);
    if (g.blocked) return { success:false, data:null, message:g.reason };
    try {
      const res = await apiService.get(`wallet/${userId}/overview`);
      return { success:true, data:res.data };
    } catch(e) { return { success:false, data:null, message:e.message||'Failed to fetch wallet overview' }; }
  },

  // ── Balance ────────────────────────────────────────────────────────────────
  getBalance: async (userId) => {
    const g = _guard(['admin','super_admin','finance_manager','accountant']);
    if (g.blocked) return { success:false, data:null, message:g.reason };
    try {
      const res = await apiService.get(`wallet/${userId}/balance`);
      return { success:true, data:res.data };
    } catch(e) { return { success:false, data:null, message:e.message||'Failed to fetch balance' }; }
  },

  // ── Add Money ──────────────────────────────────────────────────────────────
  addMoney: async (userId, data) => {
    const g = _guard(['admin','super_admin']);
    if (g.blocked) return { success:false, message:g.reason };
    try {
      const res = await apiService.post(`wallet/${userId}/add-money`, data);
      return { success:true, data:res.data, message:'Money added successfully' };
    } catch(e) { return { success:false, message:e.message||'Failed to add money' }; }
  },

  // ── Withdraw ───────────────────────────────────────────────────────────────
  withdraw: async (userId, data) => {
    const g = _guard(['admin','super_admin']);
    if (g.blocked) return { success:false, message:g.reason };
    try {
      const res = await apiService.post(`wallet/${userId}/withdraw`, data);
      return { success:true, data:res.data, message:'Withdrawal initiated successfully' };
    } catch(e) { return { success:false, message:e.message||'Failed to withdraw' }; }
  },

  // ── Transactions ───────────────────────────────────────────────────────────
  getTransactions: async (userId, filters={}) => {
    const g = _guard(['admin','super_admin','finance_manager','accountant']);
    if (g.blocked) return { success:false, data:[], message:g.reason };
    try {
      const qs  = new URLSearchParams(filters).toString();
      const res = await apiService.get(qs ? `wallet/${userId}/transactions?${qs}` : `wallet/${userId}/transactions`);
      return { success:true, data:res.data||[], pagination:res.pagination||{} };
    } catch(e) { return { success:false, data:[], message:e.message||'Failed to fetch transactions' }; }
  },

  // ── Payment Methods ────────────────────────────────────────────────────────
  getPaymentMethods: async (userId) => {
    const g = _guard(['admin','super_admin']);
    if (g.blocked) return { success:false, data:[], message:g.reason };
    try {
      const res = await apiService.get(`wallet/${userId}/payment-methods`);
      return { success:true, data:res.data||[] };
    } catch(e) { return { success:false, data:[], message:e.message||'Failed to fetch payment methods' }; }
  },
  addPaymentMethod: async (userId, data) => {
    const g = _guard(['admin','super_admin']);
    if (g.blocked) return { success:false, message:g.reason };
    try {
      const res = await apiService.post(`wallet/${userId}/payment-methods`, data);
      return { success:true, data:res.data, message:'Payment method added' };
    } catch(e) { return { success:false, message:e.message||'Failed to add payment method' }; }
  },
  deletePaymentMethod: async (userId, methodId) => {
    const g = _guard(['admin','super_admin']);
    if (g.blocked) return { success:false, message:g.reason };
    try {
      await apiService.delete(`wallet/${userId}/payment-methods/${methodId}`);
      return { success:true, message:'Payment method removed' };
    } catch(e) { return { success:false, message:e.message||'Failed to remove' }; }
  },

  // ── Bank Accounts ──────────────────────────────────────────────────────────
  getBankAccounts: async (userId) => {
    const g = _guard(['admin','super_admin']);
    if (g.blocked) return { success:false, data:[], message:g.reason };
    try {
      const res = await apiService.get(`wallet/${userId}/bank-accounts`);
      return { success:true, data:res.data||[] };
    } catch(e) { return { success:false, data:[], message:e.message||'Failed to fetch bank accounts' }; }
  },
  addBankAccount: async (userId, data) => {
    const g = _guard(['admin','super_admin']);
    if (g.blocked) return { success:false, message:g.reason };
    try {
      const res = await apiService.post(`wallet/${userId}/bank-accounts`, data);
      return { success:true, data:res.data, message:'Bank account added successfully' };
    } catch(e) { return { success:false, message:e.message||'Failed to add bank account' }; }
  },
  deleteBankAccount: async (userId, accountId) => {
    const g = _guard(['admin','super_admin']);
    if (g.blocked) return { success:false, message:g.reason };
    try {
      await apiService.delete(`wallet/${userId}/bank-accounts/${accountId}`);
      return { success:true, message:'Bank account removed' };
    } catch(e) { return { success:false, message:e.message||'Failed to remove bank account' }; }
  },

  // ── Cards ──────────────────────────────────────────────────────────────────
  getCards: async (userId) => {
    const g = _guard(['admin','super_admin']);
    if (g.blocked) return { success:false, data:[], message:g.reason };
    try {
      const res = await apiService.get(`wallet/${userId}/cards`);
      return { success:true, data:res.data||[] };
    } catch(e) { return { success:false, data:[], message:e.message||'Failed to fetch cards' }; }
  },
  addCard: async (userId, data) => {
    const g = _guard(['admin','super_admin']);
    if (g.blocked) return { success:false, message:g.reason };
    try {
      const res = await apiService.post(`wallet/${userId}/cards`, data);
      return { success:true, data:res.data, message:'Card added successfully' };
    } catch(e) { return { success:false, message:e.message||'Failed to add card' }; }
  },
  deleteCard: async (userId, cardId) => {
    const g = _guard(['admin','super_admin']);
    if (g.blocked) return { success:false, message:g.reason };
    try {
      await apiService.delete(`wallet/${userId}/cards/${cardId}`);
      return { success:true, message:'Card removed' };
    } catch(e) { return { success:false, message:e.message||'Failed to remove card' }; }
  },

  // ── Recurring Payments ─────────────────────────────────────────────────────
  getRecurring: async (userId) => {
    const g = _guard(['admin','super_admin']);
    if (g.blocked) return { success:false, data:[], message:g.reason };
    try {
      const res = await apiService.get(`wallet/${userId}/recurring`);
      return { success:true, data:res.data||[] };
    } catch(e) { return { success:false, data:[], message:e.message||'Failed to fetch recurring' }; }
  },
  createRecurring: async (userId, data) => {
    const g = _guard(['admin','super_admin']);
    if (g.blocked) return { success:false, message:g.reason };
    try {
      const res = await apiService.post(`wallet/${userId}/recurring`, data);
      return { success:true, data:res.data, message:'Recurring payment created' };
    } catch(e) { return { success:false, message:e.message||'Failed to create recurring' }; }
  },
  toggleRecurring: async (userId, recurringId, isActive) => {
    const g = _guard(['admin','super_admin']);
    if (g.blocked) return { success:false, message:g.reason };
    try {
      const res = await apiService.patch(`wallet/${userId}/recurring/${recurringId}`, { isActive });
      return { success:true, data:res.data, message:`Recurring payment ${isActive?'activated':'paused'}` };
    } catch(e) { return { success:false, message:e.message||'Failed to toggle' }; }
  },
  deleteRecurring: async (userId, recurringId) => {
    const g = _guard(['admin','super_admin']);
    if (g.blocked) return { success:false, message:g.reason };
    try {
      await apiService.delete(`wallet/${userId}/recurring/${recurringId}`);
      return { success:true, message:'Recurring payment cancelled' };
    } catch(e) { return { success:false, message:e.message||'Failed to cancel' }; }
  },

  // ── Rewards ────────────────────────────────────────────────────────────────
  getRewards: async (userId) => {
    const g = _guard(['admin','super_admin']);
    if (g.blocked) return { success:false, data:null, message:g.reason };
    try {
      const res = await apiService.get(`wallet/${userId}/rewards`);
      return { success:true, data:res.data };
    } catch(e) { return { success:false, data:null, message:e.message||'Failed to fetch rewards' }; }
  },
  redeemRewards: async (userId, points) => {
    const g = _guard(['admin','super_admin']);
    if (g.blocked) return { success:false, message:g.reason };
    try {
      const res = await apiService.post(`wallet/${userId}/rewards/redeem`, { points });
      return { success:true, data:res.data, message:'Rewards redeemed successfully' };
    } catch(e) { return { success:false, message:e.message||'Failed to redeem' }; }
  },

  // ── Statements ─────────────────────────────────────────────────────────────
  getStatements: async (userId, filters={}) => {
    const g = _guard(['admin','super_admin','finance_manager','accountant']);
    if (g.blocked) return { success:false, data:[], message:g.reason };
    try {
      const qs  = new URLSearchParams(filters).toString();
      const res = await apiService.get(qs ? `wallet/${userId}/statements?${qs}` : `wallet/${userId}/statements`);
      return { success:true, data:res.data||[] };
    } catch(e) { return { success:false, data:[], message:e.message||'Failed to fetch statements' }; }
  },

  // ── Limits & KYC ──────────────────────────────────────────────────────────
  getLimitsKyc: async (userId) => {
    const g = _guard(['admin','super_admin']);
    if (g.blocked) return { success:false, data:null, message:g.reason };
    try {
      const res = await apiService.get(`wallet/${userId}/limits`);
      return { success:true, data:res.data };
    } catch(e) { return { success:false, data:null, message:e.message||'Failed to fetch limits' }; }
  },
  updateLimits: async (userId, data) => {
    const g = _guard(['admin','super_admin']);
    if (g.blocked) return { success:false, message:g.reason };
    try {
      const res = await apiService.put(`wallet/${userId}/limits`, data);
      return { success:true, data:res.data, message:'Limits updated successfully' };
    } catch(e) { return { success:false, message:e.message||'Failed to update limits' }; }
  },
  submitKyc: async (userId, data) => {
    const g = _guard(['admin','super_admin']);
    if (g.blocked) return { success:false, message:g.reason };
    try {
      const res = await apiService.post(`wallet/${userId}/kyc`, data);
      return { success:true, data:res.data, message:'KYC submitted successfully' };
    } catch(e) { return { success:false, message:e.message||'Failed to submit KYC' }; }
  },

  // ── Key Wallet ─────────────────────────────────────────────────────────────
  getKeyWallet: async (userId, filters={}) => {
    const g = _guard(['admin','super_admin']);
    if (g.blocked) return { success:false, data:[], message:g.reason };
    try {
      const qs  = new URLSearchParams(filters).toString();
      const res = await apiService.get(qs ? `wallet/${userId}/keys?${qs}` : `wallet/${userId}/keys`);
      return { success:true, data:res.data||[], pagination:res.pagination||{} };
    } catch(e) { return { success:false, data:[], message:e.message||'Failed to fetch keys' }; }
  },
  generateKey: async (userId, data) => {
    const g = _guard(['admin','super_admin']);
    if (g.blocked) return { success:false, message:g.reason };
    try {
      const res = await apiService.post(`wallet/${userId}/keys/generate`, data);
      return { success:true, data:res.data, message:'Key generated and invoice created' };
    } catch(e) { return { success:false, message:e.message||'Failed to generate key' }; }
  },
  revokeKey: async (userId, keyId, reason='') => {
    const g = _guard(['admin','super_admin']);
    if (g.blocked) return { success:false, message:g.reason };
    try {
      const res = await apiService.patch(`wallet/${userId}/keys/${keyId}/revoke`, { reason });
      return { success:true, data:res.data, message:'Key revoked successfully' };
    } catch(e) { return { success:false, message:e.message||'Failed to revoke key' }; }
  },
  renewKey: async (userId, keyId, data) => {
    const g = _guard(['admin','super_admin']);
    if (g.blocked) return { success:false, message:g.reason };
    try {
      const res = await apiService.post(`wallet/${userId}/keys/${keyId}/renew`, data);
      return { success:true, data:res.data, message:'Key renewed and invoice created' };
    } catch(e) { return { success:false, message:e.message||'Failed to renew key' }; }
  },
};

export default walletController;